/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Returns true when the shopper has selected a value for every product dimension type.
 * Used by the Cross-LE flow to defer the interactive SearchProductAvailability lookup
 * until a single concrete variant is pinned, so an arbitrary partially-resolved variant's stock is
 * never surfaced. Variant resolution, price and image still update on partial selections.
 * @param productDimensions - The full list of dimension types for the product.
 * @param selectedDimensions - Map of dimension-type value to the currently selected dimension-value id.
 * @returns True when every dimension type has a truthy selection.
 */
export const areAllDimensionsSelected = (
    productDimensions: ProductDimensionFull[] | undefined,
    selectedDimensions: { [dimensionTypeValue: number]: string }
): boolean =>
    ArrayExtensions.hasElements(productDimensions) &&
    productDimensions.every(dimension => Boolean(selectedDimensions[dimension.DimensionTypeValue]));

/**
 * Determines whether the PDP inventory label can be shown for the current dimension state.
 * Cross-LE variant products suppress server-loaded inventory until a concrete variant is selected.
 * Simple products and the legacy non-Cross-LE flow preserve their existing behavior.
 * @param productDimensions - The full list of dimension types for the product.
 * @param selectedDimensions - Map of dimension-type value to the selected dimension-value id.
 * @param isCrossLegalEntityEnabled - Whether the Cross-LE ecommerce feature is enabled.
 * @returns True when inventory status can be displayed.
 */
export const shouldShowInventoryLabel = (
    productDimensions: ProductDimensionFull[] | undefined,
    selectedDimensions: { [dimensionTypeValue: number]: string },
    isCrossLegalEntityEnabled: boolean
): boolean =>
    !isCrossLegalEntityEnabled ||
    !ArrayExtensions.hasElements(productDimensions) ||
    areAllDimensionsSelected(productDimensions, selectedDimensions);
